<template>
  <header class="header" :class="{ scrolled: isScrolled }">
    <div 
      class="header-inner"
      @mouseenter="showAllSubMenus"
      @mouseleave="hideAllSubMenus"
    >
      <router-link to="/" class="logo">
        <img src="@/assets/bxLogo.png" alt="BX201 Logo" class="logo-image" />
      </router-link>
      
      <!-- PC 메뉴 -->
      <nav class="main-nav desktop" v-if="!isMobile">
        <div
          v-for="(menu, index) in menuItems"
          :key="index"
          class="menu-group"
          :ref="el => { if (el) menuRefs[index] = el }"
        >
          <!-- 메인 타이틀 클릭 시 첫 번째 자식으로 이동 -->
          <span class="main-title" @click="navigateToFirstChild(menu)">{{ menu.title }}</span>

          <!-- 각 메뉴의 서브 메뉴 (드롭다운) -->
          <div 
            v-if="showDropdown" 
            class="sub-menu"
            :style="{ width: `${menuWidths[index] || 150}px` }"
          >
            <!-- ✅ 동일 경로 클릭에도 재로딩 되도록 go() 사용 -->
            <a
              v-for="(item, idx) in menu.children"
              :key="idx"
              href="#"
              class="sub-menu-item"
              @click.prevent="() => { go(item.path); hideAllSubMenus() }"
            >
              {{ item.label }}
            </a>
          </div>
        </div>
      </nav>

      <button class="hamburger" @click="mobileOpen = !mobileOpen">
        <font-awesome-icon :icon="['fas', 'bars']" />
      </button>
    </div>

    <!-- Mobile -->
    <transition name="fade">
      <nav v-if="mobileOpen" class="mobile-nav">
        <div v-for="(menu, index) in menuItems" :key="index" class="mobile-group">
          <div class="mobile-title" @click="toggleSubmenu(index)">
            {{ menu.title }}
            <font-awesome-icon
              :icon="mobileActiveIndex === index ? ['fas', 'chevron-up'] : ['fas', 'chevron-down']"
              class="arrow" 
            />
          </div>
          <div v-if="mobileActiveIndex === index" class="mobile-submenu">
            <!-- ✅ 모바일도 go() 사용 + 메뉴 닫기 -->
            <a
              v-for="(item, idx) in menu.children"
              :key="idx"
              href="#"
              class="mobile-link"
              @click.prevent="() => { go(item.path); mobileOpen = false }"
            >
              - {{ item.label }}
            </a>
          </div>
        </div>
      </nav>
    </transition>

    <!-- 드롭다운 전체 배경 (부모로 감싸서 유지) -->
    <div 
      class="dropdown-wrapper"
      v-if="showDropdown && !isMobile"  
      @mouseenter="showAllSubMenus"
      @mouseleave="hideAllSubMenus"
    >
      <div class="dropdown-background"></div>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from "vue";
//import router from "@/router";
import { useRoute, useRouter } from "vue-router";   // ✅ useRoute 추가
import { bumpReloadKey } from '@/utils/reloader'
const router = useRouter();
const route  = useRoute();

const mobileOpen = ref(false);
const isMobile = ref(false);
const isScrolled = ref(false);
const showDropdown = ref(false);
const menuRefs = ref([]);
const menuWidths = ref([]);
const mobileActiveIndex = ref(null);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 30;
};

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
  if (!isMobile.value) {
    mobileOpen.value = false;
  }
  calculateMenuWidths();
};

// 첫 번째 자식으로 이동
const navigateToFirstChild = (menu) => {
  if (menu.children && menu.children.length > 0) {
    const firstChildPath = menu.children[0].path;
    go(firstChildPath);  // ✅ push 대신 go 사용
  }
};

const toggleSubmenu = (index) => {
  mobileActiveIndex.value = mobileActiveIndex.value === index ? null : index;
};

// 모든 서브 메뉴 보여주기
const showAllSubMenus = () => {
  showDropdown.value = true;
};

// 모든 서브 메뉴 숨기기
const hideAllSubMenus = () => {
  showDropdown.value = false;
};

// 메뉴 그룹의 너비 계산
const calculateMenuWidths = async () => {
  await nextTick();
  menuWidths.value = menuRefs.value.map((menu) =>
    menu ? menu.offsetWidth : 150
  );
};

onMounted(() => {
  handleResize();
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleScroll);
  calculateMenuWidths();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleScroll);
});
  
const menuItems = [
  {
    title: "단지안내",
    children: [
      { label: "사업안내", path: "/introduction" },
      { label: "세대평면도", path: "/environment" },
      { label: "커뮤니티시설", path: "/environment/community" },
      { label: "오시는 길", path: "/map" },
    ],
  },
  {
    title: "입주안내",
    children: [
      { label: "자격조건", path: "/requirement" },
      { label: "제출서류", path: "/requirement/documents" },
      { label: "금융지원", path: "/apply/finance" },
    ],
  },
  {
    title: "청약안내",
    children: [
      { label: "청약신청", path: "/apply" },
      { label: "청약조회", path: "/inquiry" }
    ],
  },
  {
    title: "고객센터",
    children: [
      { label: "공지사항", path: "/notice" },
      { label: "자료실", path: "/notice/resources" },
    ],
  },
];

// ✅ 동일 경로면 쿼리만 바꿔서 강제 재마운트, 아니면 push
function go(path) {
  if (route.path === path) {
    // ✅ URL 그대로, 컴포넌트만 재마운트
    bumpReloadKey()
  } else {
    router.push(path)
  }
}
</script>

<style scoped>
.logo {
  left: 20px;
  /* 추가 */
  margin-left: 0;
  padding-left: 0;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background: white;
  border-bottom: 1px solid #eee;
  z-index: 1000;
  align-items: center; /* ✅ 세로 가운데 정렬 */
  justify-content: center;
}

.header-inner {
  max-width: 1200px;
  margin: 0 auto;
  height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-image {
  width: 60px;
  height: auto;
  margin: 0 auto;
  display: flex;
  align-items: center; /* ✅ 로고도 세로 가운데 정렬 */
}

.main-nav.desktop {
  display: flex;
  align-items: center; /* ✅ 네비게이션 세로 가운데 정렬 */
  gap: 70px;
}

.menu-group {
  position: relative;
  display: flex;
  align-items: center; /* ✅ 메뉴 세로 가운데 정렬 */
  height: 100px;        /* ✅ header-inner의 height와 맞춤 */
}

.mobile-title {
  font-weight: 700;
  font-size: 1.08rem;
  padding: 24px 20px 24px 18px; /* 상,우,하,좌 */
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}


.hamburger {
  display: none;
  background: none;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
  /* 기본은 0 */
  margin-right: 0;
  padding-right: 0;
}

.hamburger svg {
  width: 24px;
  height: 24px;
}

.mobile-group {
  border-bottom: 1px solid #eee; /* 구분선 */
}

/* 드롭다운 전체 */
.sub-menu {
  position: absolute;
  top: 120%;
  left: 0;
  background-color: white;
  padding: 10px;
  border-radius: 4px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  text-align: center;
  font-size: 0.8rem;
}

.sub-menu-item {
  display: block;
  padding: 6px 12px;
  color: #333;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s;
  text-align: center;
}

.sub-menu-item:hover {
  background-color: #f0f0f0;
  color: #007bff;
}

/* 드롭다운 전체 배경 */
.dropdown-wrapper {
  position: absolute;
  top: 100px;
  left: 0;
  width: 100%;
  height: 200px;
  background-color: transparent;
  z-index: 999;
  pointer-events: none;
}

.mobile-submenu {
  padding-left: 32px; /* 안쪽 들여쓰기 */
  background: #fff;
  padding-bottom: 10px;
}

.dropdown-background {
  width: 100%;
  height: 100%;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
}
.mobile-nav {
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 0;
}

.mobile-title {
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
}

.mobile-link {
  display: block;
  color: #222;
  text-decoration: none;
  font-size: 0.98rem;
  padding: 7px 0;
  margin-bottom: 2px;
}

.mobile-link:hover {
  color: #008855;
  font-weight: bold;
  background: #f4f6fa;
  border-radius: 4px;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.arrow {
  font-size: 1.1em;
  color: #333;
  margin-left: 6px;
  transition: transform 0.2s;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .logo {
    margin-left: 10px;
    padding-left: 8px;
  }
  .hamburger {
    display: block;
    margin-right: 12px;
  }
  .main-nav.desktop {
    display: none;
  }

  /* 모바일 전용 메뉴 스타일 */
  .mobile-nav {
    display: flex;
    flex-direction: column;
    background: #fff;
    padding: 0;
  }
  .mobile-group {
    border-bottom: 1px solid #eee;
  }
  .mobile-title {
    font-weight: 700;
    font-size: 1.2rem;
    padding: 20px 16px 20px 14px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .mobile-submenu {
    padding-left: 32px;
    background: #fff;
    padding-bottom: 10px;
  }
  .mobile-link {
    display: block;
    color: #222;
    text-decoration: none;
    font-size: 0.98rem;
    padding: 7px 0;
    margin-bottom: 2px;
  }
  .mobile-link:hover {
    color: #008855;
    font-weight: bold;
    background: #f4f6fa;
    border-radius: 4px;
  }
}
</style>
